import React, { useState } from "react";
import { CaretLeft, CaretRight, CaretDoubleLeft, CaretDoubleRight } from "@phosphor-icons/react";
import { useI18n } from "../lib/i18n";

/**
 * Reusable smart pagination — i18n-aware (FR/EN/DE).
 *
 * Number formatting follows the active language (toLocaleString) so EN/DE
 * visitors don't see "1 947" with a French non-breaking thin space.
 */
export default function Pagination({ page, totalPages, total, limit, onChange, compact = false, testidPrefix = "pg" }) {
  const { t, lang } = useI18n();
  const [gotoValue, setGotoValue] = useState("");
  const locale = lang === "fr" ? "fr-FR" : lang === "de" ? "de-DE" : "en-US";

  if (!totalPages || totalPages <= 1) return null;

  const buildPages = () => {
    if (totalPages <= 10) return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (page <= 6) return [1, 2, 3, 4, 5, 6, 7, 8, "…", totalPages];
    if (page >= totalPages - 5) return [1, "…", totalPages - 7, totalPages - 6, totalPages - 5, totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    return [1, "…", page - 2, page - 1, page, page + 1, page + 2, "…", totalPages];
  };

  const submitGoto = (e) => {
    e.preventDefault();
    const p = parseInt(gotoValue);
    if (p >= 1 && p <= totalPages) {
      onChange(p);
      setGotoValue("");
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const change = (p) => {
    onChange(p);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const hasPrev = page > 1;
  const hasNext = page < totalPages;
  const startIdx = total && limit ? (page - 1) * limit + 1 : null;
  const endIdx = total && limit ? Math.min(page * limit, total) : null;

  return (
    <nav className={`flex ${compact ? "flex-col gap-2" : "flex-col md:flex-row md:items-center md:justify-between"} gap-3 bg-card border border-border rounded-sm px-3 py-2.5`} aria-label={t.pagination.label} data-testid={`${testidPrefix}-pagination`}>
      {total !== undefined && startIdx !== null && (
        <div className="text-xs text-muted-foreground">
          {t.pagination.page} <b className="text-foreground">{page}</b> {t.pagination.of} <b className="text-foreground">{totalPages.toLocaleString(locale)}</b>
          {" — "}{t.pagination.results}{" "}
          <b className="text-foreground">{startIdx.toLocaleString(locale)}</b>
          {" "}{t.pagination.to}{" "}
          <b className="text-foreground">{endIdx.toLocaleString(locale)}</b>
          {" "}{t.pagination.total_of}{" "}
          <b className="text-foreground">{total.toLocaleString(locale)}</b>
        </div>
      )}
      <div className="flex items-center gap-1 flex-wrap">
        <PageBtn disabled={!hasPrev} onClick={() => change(1)} testid={`${testidPrefix}-first`} aria-label={t.pagination.first}><CaretDoubleLeft size={12} weight="bold"/></PageBtn>
        <PageBtn disabled={!hasPrev} onClick={() => change(page - 1)} testid={`${testidPrefix}-prev`} aria-label={t.pagination.previous}>
          <CaretLeft size={12} weight="bold"/>
          {!compact && <span className="ml-1 text-xs">{t.pagination.previous}</span>}
        </PageBtn>
        {buildPages().map((p, i) => p === "…"
          ? <span key={`gap-${i}`} className="px-2 text-xs text-muted-foreground select-none">…</span>
          : <PageBtn key={p} active={p === page} onClick={() => change(p)} testid={`${testidPrefix}-${p}`}>{p}</PageBtn>
        )}
        <PageBtn disabled={!hasNext} onClick={() => change(page + 1)} testid={`${testidPrefix}-next`} aria-label={t.pagination.next}>
          {!compact && <span className="mr-1 text-xs">{t.pagination.next}</span>}
          <CaretRight size={12} weight="bold"/>
        </PageBtn>
        <PageBtn disabled={!hasNext} onClick={() => change(totalPages)} testid={`${testidPrefix}-last`} aria-label={t.pagination.last}><CaretDoubleRight size={12} weight="bold"/></PageBtn>
        <form onSubmit={submitGoto} className="flex items-center gap-1 ml-2">
          <input
            type="number"
            min="1"
            max={totalPages}
            placeholder={t.pagination.goto_placeholder}
            value={gotoValue}
            onChange={e => setGotoValue(e.target.value)}
            className="w-24 text-xs bg-background border border-border rounded-sm px-2 py-1.5 focus:border-primary outline-none"
            data-testid={`${testidPrefix}-goto-input`}
            aria-label={t.pagination.goto_aria}
          />
          <button type="submit" className="text-xs px-3 py-1.5 bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white rounded-sm font-semibold" data-testid={`${testidPrefix}-goto-btn`}>OK</button>
        </form>
      </div>
    </nav>
  );
}

function PageBtn({ children, active, disabled, onClick, testid, ...rest }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      {...rest}
      className={`min-w-[32px] h-8 px-2 text-xs rounded-sm border inline-flex items-center justify-center ${
        active ? "bg-primary text-primary-foreground border-primary font-bold" :
        disabled ? "border-border text-muted-foreground/40 cursor-not-allowed" :
        "border-border hover:bg-accent text-foreground"
      }`}
    >{children}</button>
  );
}
